import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Users2, BookOpen, MapPin } from 'lucide-react';
import { PremiumPageHeader } from '@repo/ui';

const GroupsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: groups, isLoading } = useQuery({
    queryKey: ['student-groups'],
    queryFn: () => api.get('/student/groups').then((res) => res.data?.data),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PremiumPageHeader
        title={t('student.groups.title')}
        subtitle={t('student.groups.subtitle', {
          defaultValue: 'Explore your enrolled groups and tracks',
        })}
        icon={Users2}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {!groups?.length ? (
        <div className="text-center py-16 text-muted-foreground">
          {t('student.groups.noGroups')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((group: any) => (
            <div
              key={group._id}
              onClick={() => navigate(`/groups/${group._id}`)}
              className="rounded-xl border border-border bg-card p-5 shadow-sm cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-lg truncate">{group.name}</h3>
                  <p className="text-sm text-muted-foreground font-mono">{group.code}</p>
                </div>
                {group.label && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary whitespace-nowrap">
                    {group.label.name}
                  </span>
                )}
              </div>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                {group.program?.track && (
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 shrink-0" />
                    <span className="truncate">{group.program.track.name}</span>
                  </div>
                )}
                {group.branch && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span className="truncate">{group.branch.nameEn}</span>
                  </div>
                )}
                {(group.mentor || group.instructor) && (
                  <div className="flex items-center gap-4 flex-wrap text-xs mt-2 pt-2 border-t border-border">
                    {group.mentor && (
                      <span>
                        {t('student.groups.mentor')}: <strong>{group.mentor.name}</strong>
                      </span>
                    )}
                    {group.instructor && (
                      <span>
                        {t('student.groups.instructor')}: <strong>{group.instructor.name}</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupsPage;
